import React from 'react';
import { DatePicker, Form, Input, Modal, Radio, Spin, Typography } from 'antd';
import { DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import { CLOSING_PACKAGE_MODAL_TITLES } from '../../workspace/closingPackageModalKinds';
import { useConfirmEdoReceiptModal } from '../../hooks/useConfirmEdoReceiptModal';
import ClosingPackageTaskModalContext from './ClosingPackageTaskModalContext';
import ClosingPackageTaskModalEventDateHint from './ClosingPackageTaskModalEventDateHint';
import ClosingPackageTaskModalFooter from './ClosingPackageTaskModalFooter';
import type { ClosingPackageTaskModalBaseProps } from './ClosingPackageFormationModal.types';

const { Text } = Typography;

export type ClosingPackageConfirmEdoReceiptModalProps = ClosingPackageTaskModalBaseProps & {
  onSuccess?: () => void | Promise<void>;
};

const ClosingPackageConfirmEdoReceiptModal: React.FC<ClosingPackageConfirmEdoReceiptModalProps> = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}) => {
  const {
    form,
    projection,
    contextView,
    contextLoading,
    receiptResult,
    commentRequired,
    commentLabel,
    submitting,
    submitLabel,
    handleSubmit,
  } = useConfirmEdoReceiptModal({
    applicationId,
    open,
    onClose,
    onSuccess,
  });

  return (
    <Modal
      title={CLOSING_PACKAGE_MODAL_TITLES['confirm-edo-receipt']}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={760}
      data-testid="confirm-edo-receipt-modal"
      footer={
        <ClosingPackageTaskModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={submitLabel}
          submitTestId="confirm-edo-receipt-submit"
        />
      }
    >
      {contextLoading || !projection ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <ClosingPackageTaskModalContext contextView={contextView} />

          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Бухгалтер подтверждает, что клиент принял документы в ЭДО. Это не бумажное получение
            оригиналов.
          </Text>

          <Form form={form} layout="vertical">
            <Form.Item
              name="result"
              label="Результат проверки ЭДО"
              rules={[{ required: true, message: 'Выберите результат' }]}
            >
              <Radio.Group data-testid="confirm-edo-receipt-result">
                <Radio value="RECEIVED">Клиент принял документы</Radio>
                <Radio value="RESEND">Клиент ещё не принял — повторить отправку</Radio>
                <Radio value="REVISION">Требуется исправление комплекта</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="receivedAt"
              label={
                receiptResult === 'RESEND' || receiptResult === 'REVISION'
                  ? 'Дата проверки'
                  : 'Дата подтверждения'
              }
              rules={[
                {
                  required: true,
                  message:
                    receiptResult === 'RESEND' || receiptResult === 'REVISION'
                      ? 'Укажите дату проверки'
                      : 'Укажите дату подтверждения',
                },
              ]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                data-testid="confirm-edo-receipt-date"
                format={DOCUMENT_UI_DATETIME_FORMAT}
              />
            </Form.Item>

            <Form.Item
              name="comment"
              label={commentLabel}
              rules={
                commentRequired
                  ? [
                      {
                        required: true,
                        message:
                          receiptResult === 'REVISION'
                            ? 'Укажите причину доработки'
                            : 'Укажите комментарий',
                      },
                      { whitespace: true, message: 'Комментарий не может быть пустым' },
                    ]
                  : []
              }
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder={
                  receiptResult === 'REVISION'
                    ? 'Опишите, что нужно исправить в комплекте'
                    : 'Комментарий для журнала комплекта'
                }
                data-testid="confirm-edo-receipt-comment"
              />
            </Form.Item>
          </Form>

          <ClosingPackageTaskModalEventDateHint />
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageConfirmEdoReceiptModal;
